import {
  Button,
  ButtonGroup,
  InputGroup,
  Slider,
  Tab,
  Tabs,
} from '@blueprintjs/core'
import debounce from 'lodash.debounce'
import React from 'react'
import styled from 'styled-components'

import User from '../api/user'
import LoadingText from '../components/LoadingText'
import UserListing from '../components/UserListing'
import { failure, success } from '../toaster'

const Listing = styled.div`
  margin-top: 1rem;
`

const ListingTabs = styled(Tabs)`
  margin: 1rem 0;
`

const PaginatorGroup = styled(ButtonGroup)`
  margin: 1rem 0;
`

const SmallSlider = styled(Slider)`
  max-width: 250px;
`

export default class Users extends React.Component {
  state = {
    loading: false,
    page: 0,
    pagination: { total: 0 },
    perPage: 10,
    query: '',
    selectedFilter: 'user-active-tab',
    users: [],
  }

  componentDidMount() {
    this.searchForUsers()
  }

  searchForUsersDebounced = debounce(() => {
    return this.searchForUsers()
  }, 500)

  searchForUsers = async () => {
    this.setState({ loading: true })
    await this.search()
    this.setState({ loading: false })
  }

  async search() {
    const users = await User.search({
      active: this.state.selectedFilter === 'user-active-tab',
      page: this.state.page,
      per_page: this.state.perPage,
      query: this.state.query,
    })

    this.setState({
      pagination: users.pagination,
      users: users.results,
    })
  }

  renderListing() {
    if (this.state.loading) {
      return <LoadingText>Loading users...</LoadingText>
    }

    if (this.state.users.length === 0) {
      return 'Nothing here.'
    }

    return this.state.users.map((user) => (
      <UserListing
        key={user.id}
        user={user}
        onDelete={() => this.handleDeleteUser(user)}
        onSendEmail={() => this.handleSendEmail(user)}
        onEnable={() => this.handleChangeActivationState(user, true)}
        onDisable={() => this.handleChangeActivationState(user, false)}
      />
    ))
  }

  render() {
    const maxIndex = this.state.pagination.total - 1
    const total = maxIndex + 1

    return (
      <>
        <InputGroup
          leftIcon="search"
          placeholder="Search by username or ID..."
          type="search"
          large={true}
          onChange={this.handleQueryChange}
          value={this.state.query}
        />
        <ListingTabs
          id="user-filter-tabs"
          onChange={this.handleFilterChange}
          selectedTabId={this.state.selectedFilter}
        >
          <Tab title="Active" id="user-active-tab" />
          <Tab title="Inactive" id="user-inactive-tab" />
        </ListingTabs>
        <Listing>{this.renderListing()}</Listing>
        <PaginatorGroup>
          <Button
            icon="arrow-left"
            disabled={this.state.page === 0}
            onClick={this.handleBack}
          />
          {this.state.page !== 0 ? (
            <Button
              disabled={this.state.page === 0}
              onClick={this.handleFirstPageButtonClick}
            >
              1
            </Button>
          ) : null}
          <Button disabled={true}>{this.state.page + 1}</Button>
          {this.state.page !== maxIndex && total !== 0 ? (
            <Button
              disabled={this.state.page === maxIndex}
              onClick={this.handleLastPageButtonClick}
            >
              {total}
            </Button>
          ) : null}
          <Button
            icon="arrow-right"
            disabled={this.state.page === maxIndex || total === 0}
            onClick={this.handleForward}
          />
        </PaginatorGroup>
        <SmallSlider
          stepSize={5}
          max={50}
          min={5}
          labelStepSize={5}
          value={this.state.perPage}
          onChange={this.handlePerPageChange}
          onRelease={this.searchForUsers}
        />
      </>
    )
  }

  handleFirstPageButtonClick = () => {
    this.handlePageChange(0)
  }

  handleLastPageButtonClick = () => {
    this.handlePageChange(this.state.pagination.total - 1)
  }

  handleQueryChange = (event) => {
    this.setState({ page: 0, query: event.target.value })
    this.searchForUsersDebounced()
  }

  handleFilterChange = (newTabId) => {
    this.setState({ page: 0, selectedFilter: newTabId }, this.searchForUsers)
  }

  handleSendEmail = async (user) => {
    try {
      await user.sendEmail()
      success(`Sent email to ${user.name}.`, 'envelope')
    } catch (err) {
      failure(`Failed to send email: ${err}`)
    }
  }

  handleChangeActivationState = async (user, newState: boolean) => {
    await user.changeActivationState(newState)
    this.searchForUsers()
  }

  handleDeleteUser = async (user) => {
    try {
      await user.delete()
      success(`Deleted ${user.name}.`, 'trash')
    } catch (err) {
      failure(`Failed to delete ${user.name}: ${err}`)
    }
    this.searchForUsers()
  }

  handlePageChange = (to) => {
    this.setState({ page: to }, this.searchForUsers)
  }

  handleBack = () => {
    this.setState(
      {
        page: Math.max(0, this.state.page - 1),
      },
      this.searchForUsers
    )
  }

  handleForward = () => {
    this.setState(
      {
        page: Math.min(this.state.pagination.total - 1, this.state.page + 1),
      },
      this.searchForUsers
    )
  }

  handlePerPageChange = (value) => {
    this.setState({
      page: 0,
      perPage: value,
    })
  }
}
